import React from 'react';
import { AnyService } from '../../../types/service.type';
import { getServiceAge, getHealthColor, getHealthBadge } from '../../../utils/serviceHelpers';
import { getServiceConfig } from '../../../config/serviceConfig';

interface ServiceCardBodyProps {
  service: AnyService;
  variant: 'compact' | 'default' | 'detailed';
  renderExtra?: (service: AnyService) => React.ReactNode;
}

const ServiceCardBody: React.FC<ServiceCardBodyProps> = ({ 
  service, 
  variant,
  renderExtra 
}) => {
  const config = getServiceConfig(service.type);
  const showDetails = variant !== 'compact';

  if (variant === 'compact') {
    return (
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>{config.label}</span>
        <span>{getServiceAge(service.createdAt)}</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Metadata Grid */}
      {showDetails && (
        <div className={`
          grid gap-3
          ${variant === 'detailed' ? 'grid-cols-2' : 'grid-cols-1'}
        `}>
          {/* Instance Type */}
          {service.instanceType && (
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Instance Type</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{service.instanceType}</p>
            </div>
          )}

          {/* Region */}
          {service.region && (
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Region</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{service.region}</p>
            </div>
          )}

          {/* Environment */}
          {service.environment && (
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400">Environment</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                {service.environment}
              </p>
            </div>
          )}

          {/* Health */}
          <div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Health</span>
            <p className={`text-sm font-medium ${getHealthColor(service.health)}`}>
              {getHealthBadge(service.health)} {service.health}
            </p>
          </div>
        </div>
      )}

      {/* Metadata básica para variant default */}
      {variant === 'default' && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>{config.label}</span>
          <span className="text-xs text-gray-500">{getServiceAge(service.createdAt)}</span>
        </div>
      )}

      {/* Extra Content */}
      {renderExtra && (
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
          {renderExtra(service)}
        </div>
      )}

      {/* Tags */}
      {variant === 'detailed' && service.tags && service.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {service.tags.map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-md"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServiceCardBody;